// Import admin types
import type {
  AddUserCompanyRequest,
  AddUserCompanyResponse,
  AuditLogEntry,
  AuditLogQuery,
  FeatureFlagResource,
  ImpersonationHistoryEntry,
  ImpersonationStatus,
  SubscriptionPlanResource,
  SubscriptionResource,
  SystemSettingsResource,
  UpdateSystemSettingsRequest,
  UsageResource,
} from '@interfaces/admin/admin.types';
import type { UserResource } from '@interfaces/user/user.types';
import { createSlice } from '@reduxjs/toolkit';
import type { AppDispatch, AppState } from '@store/appStore.ts';
import { meLogOut } from '@store/reducers/meReducer.ts';
import { thunkDelete } from '@store/reducers/reducerHelpers/thunkDelete.ts';
import { thunkGet } from '@store/reducers/reducerHelpers/thunkGet.ts';
import { thunkPost } from '@store/reducers/reducerHelpers/thunkPost.ts';
import { thunkPut } from '@store/reducers/reducerHelpers/thunkPut.ts';

export interface AdminReducerState {
  // System settings
  systemSettings: SystemSettingsResource | null;
  systemSettingsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  systemSettingsError: string | null;

  // User and company management
  adminUsers: Record<string, UserResource>;
  adminCompanies: Record<string, unknown>; // TODO: Use proper Company type from company.types.ts
  userCompanyCreationStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  userCompanyCreationError: string | null;
  lastCreatedUserCompany: AddUserCompanyResponse | null;

  // Audit logs (future)
  auditLogs: Record<string, AuditLogEntry>;
  auditLogStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  auditLogError: string | null;
  auditLogQuery: AuditLogQuery | null;

  // Impersonation state
  impersonationStatus: ImpersonationStatus | null;
  impersonationHistory: Record<string, ImpersonationHistoryEntry>;
  availableUsersForImpersonation: Record<string, UserResource>;
  impersonationError: string | null;

  // Feature flags
  allFeatures: Record<string, FeatureFlagResource>;
  companyFeatures: Record<string, Record<string, FeatureFlagResource>>; // companyId -> features
  featureFlagsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  featureFlagsError: string | null;

  // Subscription management
  subscriptionPlans: Record<string, SubscriptionPlanResource>;
  companySubscriptions: Record<string, SubscriptionResource>;
  usageData: Record<string, UsageResource>;
  subscriptionStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  subscriptionError: string | null;

  // General admin state
  isInitialized: boolean;
}

const initialState: AdminReducerState = {
  systemSettings: null,
  systemSettingsStatus: 'idle',
  systemSettingsError: null,
  adminUsers: {},
  adminCompanies: {},
  userCompanyCreationStatus: 'idle',
  userCompanyCreationError: null,
  lastCreatedUserCompany: null,
  auditLogs: {},
  auditLogStatus: 'idle',
  auditLogError: null,
  auditLogQuery: null,
  impersonationStatus: null,
  impersonationHistory: {},
  availableUsersForImpersonation: {},
  impersonationError: null,
  allFeatures: {},
  companyFeatures: {},
  featureFlagsStatus: 'idle',
  featureFlagsError: null,
  subscriptionPlans: {},
  companySubscriptions: {},
  usageData: {},
  subscriptionStatus: 'idle',
  subscriptionError: null,
  isInitialized: false,
};

// Super Admin Thunks
export const getSystemSettings = thunkGet.get<undefined>('admin', '/api/v1/admin/system/settings');

export const updateSystemSettings = thunkPut.put<UpdateSystemSettingsRequest>('admin', '/api/v1/admin/system/settings');

export const addUserCompany = thunkPost.post<AddUserCompanyRequest>('admin', '/api/v1/admin/user-company');

// Future audit log endpoints
export const getAuditLogs = thunkGet.get<AuditLogQuery>('admin', '/api/v1/admin/audit-logs');

export const getAuditLogsByUser = thunkGet.get<{ userUuid: string }>(
  'admin',
  '/api/v1/admin/audit-logs/user/{userUuid}',
);

export const getAuditLogsByCompany = thunkGet.get<{ companyUuid: string }>(
  'admin',
  '/api/v1/admin/audit-logs/company/{companyUuid}',
);

// System management actions
export const enableMaintenanceMode = thunkPost.post('admin', '/api/v1/admin/system/maintenance/enable');

export const disableMaintenanceMode = thunkPost.post('admin', '/api/v1/admin/system/maintenance/disable');

export const clearSystemCache = thunkPost.post('admin', '/api/v1/admin/system/cache/clear');

// Impersonation endpoints
export const getImpersonationStatus = thunkGet.get<undefined>('admin', '/api/v1/sa/impersonation/status');

export const getImpersonationUsers = thunkGet.get<undefined>('admin', '/api/v1/sa/impersonation/users');

export const getImpersonationHistory = thunkGet.get<undefined>('admin', '/api/v1/sa/impersonation/history');

export const startImpersonation = thunkPost.post<{ userId: string }>(
  'admin',
  '/api/v1/sa/impersonation/start/{userId}',
);

export const stopImpersonation = thunkDelete.del('admin', '/api/v1/sa/impersonation/stop');

// Feature flag management
export const getAllFeatures = thunkGet.get<undefined>('admin', '/api/v1/sa/features');

export const getCompanyFeatures = thunkGet.get<{ company: string }>('admin', '/api/v1/sa/c/{company}/features');

export const toggleCompanyFeature = thunkPut.put<{ company: string; feature: string; enabled: boolean }>(
  'admin',
  '/api/v1/sa/c/{company}/features/{feature}',
);

export const bulkUpdateCompanyFeatures = thunkPost.post<{ company: string; features: Record<string, boolean> }>(
  'admin',
  '/api/v1/sa/c/{company}/features/bulk',
);

// Subscription management
export const getSubscriptionPlans = thunkGet.get<undefined>('admin', '/api/v1/sa/subscription-plans');

export const createSubscriptionPlan = thunkPost.post<SubscriptionPlanResource>(
  'admin',
  '/api/v1/sa/subscription-plans',
);

export const getCompanySubscription = thunkGet.get<{ company: string }>('admin', '/api/v1/sa/c/{company}/subscription');

export const subscribeCompany = thunkPost.post<{ company: string; planId: string }>(
  'admin',
  '/api/v1/sa/c/{company}/subscribe',
);

export const updateCompanySubscription = thunkPut.put<{
  company: string;
  subscriptionData: Partial<SubscriptionResource>;
}>('admin', '/api/v1/sa/c/{company}/subscription');

export const cancelCompanySubscription = thunkDelete.del<{ company: string }>(
  'admin',
  '/api/v1/sa/c/{company}/subscription',
);

export const getCompanyUsage = thunkGet.get<{ company: string }>('admin', '/api/v1/sa/c/{company}/usage');

export const getSystemSubscriptionUsage = thunkGet.get<undefined>('admin', '/api/v1/sa/subscription/usage');

// Super admin user creation
export const createSuperAdminUser = thunkPost.post<{ userData: UserResource }>('admin', '/api/v1/sa/user');

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearAdminState: (state) => {
      state.systemSettings = null;
      state.adminUsers = {};
      state.adminCompanies = {};
      state.auditLogs = {};
      state.lastCreatedUserCompany = null;
      state.auditLogQuery = null;
      state.impersonationStatus = null;
      state.impersonationHistory = {};
      state.availableUsersForImpersonation = {};
      state.allFeatures = {};
      state.companyFeatures = {};
      state.subscriptionPlans = {};
      state.companySubscriptions = {};
      state.usageData = {};
      state.isInitialized = false;
      // Reset all status to idle
      state.systemSettingsStatus = 'idle';
      state.userCompanyCreationStatus = 'idle';
      state.auditLogStatus = 'idle';
      state.featureFlagsStatus = 'idle';
      state.subscriptionStatus = 'idle';
      // Clear all errors
      state.systemSettingsError = null;
      state.userCompanyCreationError = null;
      state.auditLogError = null;
      state.impersonationError = null;
      state.featureFlagsError = null;
      state.subscriptionError = null;
    },
    setAuditLogQuery: (state, action) => {
      state.auditLogQuery = action.payload;
    },
    markAdminInitialized: (state) => {
      state.isInitialized = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Logout action
      .addCase(meLogOut.fulfilled, (state) => {
        state.systemSettings = null;
        state.adminUsers = {};
        state.adminCompanies = {};
        state.auditLogs = {};
        state.lastCreatedUserCompany = null;
        state.auditLogQuery = null;
        state.isInitialized = false;
        state.systemSettingsStatus = 'idle';
        state.userCompanyCreationStatus = 'idle';
        state.auditLogStatus = 'idle';
        state.systemSettingsError = null;
        state.userCompanyCreationError = null;
        state.auditLogError = null;
      })

      // System Settings
      .addCase(getSystemSettings.pending, (state) => {
        state.systemSettingsStatus = 'loading';
        state.systemSettingsError = null;
      })
      .addCase(getSystemSettings.fulfilled, (state, action) => {
        state.systemSettingsStatus = 'succeeded';
        state.systemSettings = action.payload;
      })
      .addCase(getSystemSettings.rejected, (state, action) => {
        state.systemSettingsStatus = 'failed';
        state.systemSettingsError = action.error.message || 'Failed to load system settings';
      })

      // Update System Settings
      .addCase(updateSystemSettings.pending, (state) => {
        state.systemSettingsStatus = 'loading';
        state.systemSettingsError = null;
      })
      .addCase(updateSystemSettings.fulfilled, (state, action) => {
        state.systemSettingsStatus = 'succeeded';
        state.systemSettings = action.payload;
      })
      .addCase(updateSystemSettings.rejected, (state, action) => {
        state.systemSettingsStatus = 'failed';
        state.systemSettingsError = action.error.message || 'Failed to update system settings';
      })

      // Add User Company
      .addCase(addUserCompany.pending, (state) => {
        state.userCompanyCreationStatus = 'loading';
        state.userCompanyCreationError = null;
      })
      .addCase(addUserCompany.fulfilled, (state, action) => {
        state.userCompanyCreationStatus = 'succeeded';
        state.lastCreatedUserCompany = action.payload;
        // Store the created user and company
        if (action.payload.user) {
          state.adminUsers[action.payload.user.uuid] = action.payload.user;
        }
        if (action.payload.company) {
          state.adminCompanies[action.payload.company.uuid] = action.payload.company;
        }
      })
      .addCase(addUserCompany.rejected, (state, action) => {
        state.userCompanyCreationStatus = 'failed';
        state.userCompanyCreationError = action.error.message || 'Failed to create user and company';
      })

      // Audit Logs
      .addCase(getAuditLogs.pending, (state) => {
        state.auditLogStatus = 'loading';
        state.auditLogError = null;
      })
      .addCase(getAuditLogs.fulfilled, (state, action) => {
        state.auditLogStatus = 'succeeded';
        if (Array.isArray(action.payload)) {
          action.payload.forEach((log: AuditLogEntry) => {
            state.auditLogs[log.uuid] = log;
          });
        }
      })
      .addCase(getAuditLogs.rejected, (state, action) => {
        state.auditLogStatus = 'failed';
        state.auditLogError = action.error.message || 'Failed to load audit logs';
      })

      // Audit Logs by User
      .addCase(getAuditLogsByUser.fulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          action.payload.forEach((log: AuditLogEntry) => {
            state.auditLogs[log.uuid] = log;
          });
        }
      })

      // Audit Logs by Company
      .addCase(getAuditLogsByCompany.fulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          action.payload.forEach((log: AuditLogEntry) => {
            state.auditLogs[log.uuid] = log;
          });
        }
      })

      // System Management Actions
      .addCase(enableMaintenanceMode.fulfilled, (state) => {
        if (state.systemSettings) {
          state.systemSettings.maintenanceMode = true;
        }
      })

      .addCase(disableMaintenanceMode.fulfilled, (state) => {
        if (state.systemSettings) {
          state.systemSettings.maintenanceMode = false;
        }
      })

      // Impersonation
      .addCase(getImpersonationStatus.fulfilled, (state, action) => {
        state.impersonationStatus = action.payload;
      })
      .addCase(getImpersonationUsers.fulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          action.payload.forEach((user: UserResource) => {
            state.availableUsersForImpersonation[user.uuid] = user;
          });
        }
      })
      .addCase(getImpersonationHistory.fulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          action.payload.forEach((entry: ImpersonationHistoryEntry) => {
            if (entry.id) {
              state.impersonationHistory[entry.id] = entry;
            }
          });
        }
      })
      .addCase(startImpersonation.fulfilled, (state, action) => {
        state.impersonationStatus = action.payload;
      })
      .addCase(stopImpersonation.fulfilled, (state) => {
        state.impersonationStatus = null;
      })

      // Feature flags
      .addCase(getAllFeatures.pending, (state) => {
        state.featureFlagsStatus = 'loading';
        state.featureFlagsError = null;
      })
      .addCase(getAllFeatures.fulfilled, (state, action) => {
        state.featureFlagsStatus = 'succeeded';
        if (Array.isArray(action.payload)) {
          action.payload.forEach((feature: FeatureFlagResource) => {
            state.allFeatures[feature.key] = feature;
          });
        }
      })
      .addCase(getAllFeatures.rejected, (state, action) => {
        state.featureFlagsStatus = 'failed';
        state.featureFlagsError = action.error.message || 'Failed to load features';
      })
      .addCase(getCompanyFeatures.fulfilled, (state, action) => {
        const companyId = (action.meta.arg?.urlVarsReplaceMap as { company: string } | undefined)?.company;
        if (companyId && Array.isArray(action.payload)) {
          state.companyFeatures[companyId] = {};
          action.payload.forEach((feature: FeatureFlagResource) => {
            state.companyFeatures[companyId][feature.key] = feature;
          });
        }
      })
      .addCase(toggleCompanyFeature.fulfilled, (state, action) => {
        const company = (action.meta.arg?.urlVarsReplaceMap as { company: string; feature: string } | undefined)
          ?.company;
        const feature = (action.meta.arg?.urlVarsReplaceMap as { company: string; feature: string } | undefined)
          ?.feature;
        if (company && feature && state.companyFeatures[company] && state.companyFeatures[company][feature]) {
          state.companyFeatures[company][feature] = action.payload;
        }
      })

      // Subscription management
      .addCase(getSubscriptionPlans.pending, (state) => {
        state.subscriptionStatus = 'loading';
        state.subscriptionError = null;
      })
      .addCase(getSubscriptionPlans.fulfilled, (state, action) => {
        state.subscriptionStatus = 'succeeded';
        if (Array.isArray(action.payload)) {
          action.payload.forEach((plan: SubscriptionPlanResource) => {
            state.subscriptionPlans[plan.planId] = plan;
          });
        }
      })
      .addCase(getSubscriptionPlans.rejected, (state, action) => {
        state.subscriptionStatus = 'failed';
        state.subscriptionError = action.error.message || 'Failed to load subscription plans';
      })
      .addCase(getCompanySubscription.fulfilled, (state, action) => {
        const companyId = (action.meta.arg?.urlVarsReplaceMap as { company: string } | undefined)?.company;
        if (companyId) {
          state.companySubscriptions[companyId] = action.payload;
        }
      })
      .addCase(getCompanyUsage.fulfilled, (state, action) => {
        const companyId = (action.meta.arg?.urlVarsReplaceMap as { company: string } | undefined)?.company;
        if (companyId) {
          state.usageData[companyId] = action.payload;
        }
      })
      .addCase(getSystemSubscriptionUsage.fulfilled, (state, action) => {
        state.usageData.system = action.payload;
      });
  },
});

export const { clearAdminState, setAuditLogQuery, markAdminInitialized } = adminSlice.actions;

// Selectors
export const selectSystemSettings = (state: AppState) => state.admin.systemSettings;
export const selectSystemSettingsStatus = (state: AppState) => state.admin.systemSettingsStatus;
export const selectSystemSettingsError = (state: AppState) => state.admin.systemSettingsError;

export const selectAdminUsers = (state: AppState) => state.admin.adminUsers;
export const selectAdminCompanies = (state: AppState) => state.admin.adminCompanies;
export const selectUserCompanyCreationStatus = (state: AppState) => state.admin.userCompanyCreationStatus;
export const selectUserCompanyCreationError = (state: AppState) => state.admin.userCompanyCreationError;
export const selectLastCreatedUserCompany = (state: AppState) => state.admin.lastCreatedUserCompany;

export const selectAuditLogs = (state: AppState) => state.admin.auditLogs;
export const selectAuditLogStatus = (state: AppState) => state.admin.auditLogStatus;
export const selectAuditLogError = (state: AppState) => state.admin.auditLogError;
export const selectAuditLogQuery = (state: AppState) => state.admin.auditLogQuery;

export const selectIsAdminInitialized = (state: AppState) => state.admin.isInitialized;

// Helper selectors
export const selectAuditLogsByUser = (userUuid: string) => (state: AppState) =>
  Object.values(state.admin.auditLogs).filter((log) => log.userUuid === userUuid);

export const selectAuditLogsByCompany = (companyUuid: string) => (state: AppState) =>
  Object.values(state.admin.auditLogs).filter((log) => log.companyUuid === companyUuid);

export const selectAuditLogsByAction = (action: string) => (state: AppState) =>
  Object.values(state.admin.auditLogs).filter((log) => log.action === action);

export const selectIsMaintenanceModeEnabled = (state: AppState) => state.admin.systemSettings?.maintenanceMode || false;

export const selectIsRegistrationEnabled = (state: AppState) =>
  state.admin.systemSettings?.registrationEnabled !== false; // Default to true

// Impersonation selectors
export const selectImpersonationStatus = (state: AppState) => state.admin.impersonationStatus;
export const selectImpersonationHistory = (state: AppState) => state.admin.impersonationHistory;
export const selectAvailableUsersForImpersonation = (state: AppState) => state.admin.availableUsersForImpersonation;
export const selectImpersonationError = (state: AppState) => state.admin.impersonationError;
export const selectIsCurrentlyImpersonating = (state: AppState) =>
  state.admin.impersonationStatus?.isImpersonating || false;

// Feature flag selectors
export const selectAllFeatures = (state: AppState) => state.admin.allFeatures;
export const selectCompanyFeatures = (companyId: string) => (state: AppState) =>
  state.admin.companyFeatures[companyId] || {};
export const selectFeatureFlagsStatus = (state: AppState) => state.admin.featureFlagsStatus;
export const selectFeatureFlagsError = (state: AppState) => state.admin.featureFlagsError;
export const selectIsFeatureEnabled = (companyId: string, featureKey: string) => (state: AppState) =>
  state.admin.companyFeatures[companyId]?.[featureKey]?.enabled || false;

// Subscription selectors
export const selectSubscriptionPlans = (state: AppState) => state.admin.subscriptionPlans;
export const selectCompanySubscriptions = (state: AppState) => state.admin.companySubscriptions;
export const selectCompanySubscription = (companyId: string) => (state: AppState) =>
  state.admin.companySubscriptions[companyId];
export const selectUsageData = (state: AppState) => state.admin.usageData;
export const selectCompanyUsage = (companyId: string) => (state: AppState) => state.admin.usageData[companyId];
export const selectSystemUsage = (state: AppState) => state.admin.usageData.system;
export const selectSubscriptionStatus = (state: AppState) => state.admin.subscriptionStatus;
export const selectSubscriptionError = (state: AppState) => state.admin.subscriptionError;

// Load initial admin data action
export const loadInitialAdminData = () => (dispatch: AppDispatch, getState: () => AppState) => {
  const state = getState();
  // Only load if user has super admin role
  const userRoles = state.me?.roles || [];
  const isSuperAdmin = userRoles.includes('super-admin');

  if (isSuperAdmin) {
    dispatch(getSystemSettings());
    dispatch(markAdminInitialized());
  }
};

export default adminSlice.reducer;
